import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, type FinishedTodo, type LaterWhen, type TodoDetail, type TodoItem } from '../../lib/api';
import { Button } from '../ui';
import CallInGhl from '../CallInGhl';
import LaterMenu from './LaterMenu';
import {
  NO_CONTACT, SHARED, dueDay, groupTodos, isOverdue, kindOf, matchesQuery, untilLabel, wordsOf, type TodoGroupKey, type TodoKind,
} from './todoFormat';

const NOT_SAVED = 'Not saved - try again.';
const DETAIL_UNREAD = "Couldn't read this task's details - try again.";

/** What a row can do. ⭐ Passed in, so a row's actions can grow (a draft link) without the row learning the API. */
export interface TodoRowActions {
  onDone: () => void;
  onDrop: () => void;
  onUndo: () => void;
  onLater: (when: LaterWhen) => void;
}
interface RowState {
  closedAs: 'done' | 'dropped' | null;
  error: string | null;
  busy: boolean;
  /** The snooze in force - the one he just chose, before the next read of the list carries it. */
  until: string | null;
}

/** Contact, the shared badge, the title and the due date in Pacific - the same on a list row and a clean-up card. */
export function TodoFacts({ todo, title, children }: { todo: TodoItem; title?: ReactNode; children?: ReactNode }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {todo.contact_name && <span className="text-sm font-semibold text-gray-900">{todo.contact_name}</span>}
        {todo.shared && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{SHARED}</span>
        )}
      </div>
      {title ?? <p className="mt-1 text-[15px] font-medium text-gray-900">{todo.title}</p>}
      <p className="mt-1 text-xs text-gray-500">
        {todo.due_at ? `Due ${dueDay(todo.due_at)}` : 'No due date'}
        {isOverdue(todo.due_at, new Date()) && <span className="ml-2 font-semibold text-red-700">Overdue</span>}
        {children}
      </p>
    </>
  );
}

/**
 * The task's own words and its Call Summary, read from GoHighLevel when he opens it.
 * ⛔ A failed read says so, with a retry - it is never shown as "no summary".
 */
function DetailPanel({ id }: { id: string }) {
  const [state, setState] = useState<
    { status: 'reading' } | { status: 'failed'; words: string } | { status: 'read'; detail: TodoDetail }
  >({ status: 'reading' });

  const read = useCallback(async () => {
    setState({ status: 'reading' });
    try {
      const { body, callSummary } = await api.todos.detail(id);
      setState({ status: 'read', detail: { body, callSummary } });
    } catch (e) {
      setState({ status: 'failed', words: wordsOf(e, DETAIL_UNREAD) });
    }
  }, [id]);
  useEffect(() => { void read(); }, [read]);

  return (
    <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
      {state.status === 'reading' && <p className="text-gray-500">Reading the call summary...</p>}
      {state.status === 'failed' && (
        <div className="flex flex-wrap items-center gap-3">
          <p role="alert" className="text-red-700">{state.words}</p>
          <Button size="sm" variant="outline" onClick={() => void read()}>Try again</Button>
        </div>
      )}
      {state.status === 'read' && (
        <div className="space-y-2">
          <p className="whitespace-pre-wrap">{state.detail.body ?? 'No note on this task.'}</p>
          <p className="whitespace-pre-wrap">{state.detail.callSummary ?? 'No call summary for this task.'}</p>
        </div>
      )}
    </div>
  );
}

function TodoRow({ todo, state, actions }: { todo: TodoItem; state: RowState; actions: TodoRowActions }) {
  const [open, setOpen] = useState(false);

  if (state.closedAs) {
    return (
      <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <span>{state.closedAs === 'done' ? 'Marked done.' : 'Dropped.'}</span>
        <Button size="sm" variant="ghost" onClick={actions.onUndo} disabled={state.busy}>Undo</Button>
        {state.error && <span role="alert" className="text-red-700">{state.error}</span>}
      </li>
    );
  }

  return (
    <li className="rounded-lg border border-gray-200 bg-white p-4">
      <TodoFacts
        todo={todo}
        title={(
          <>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              className="mt-1 block text-left text-[15px] font-medium text-gray-900 hover:underline"
            >
              {todo.title}
            </button>
            {/* ⭐ R49: his own note (saved here or through the assistant) - "add a note: waiting on Moses" must not
                vanish. His words, so shown as they are; a client's message never is. */}
            {todo.note && <p className="mt-0.5 whitespace-pre-wrap text-xs text-gray-500">{`Note: ${todo.note}`}</p>}
          </>
        )}
      >
        {state.until && <span className="ml-2">{untilLabel(state.until)}</span>}
      </TodoFacts>
      {open && <DetailPanel id={todo.ghl_task_id} />}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/* PHONE1: his to-dos are mostly calls - Call in GHL first (R9). Only with a page to open: a contactless to-do
            (R14) has nobody to call, and a contact with no page (no location id on the server) shows nothing rather
            than a false "Not in GoHighLevel". */}
        {todo.ghl_contact_url && <CallInGhl url={todo.ghl_contact_url} />}
        {todo.ghl_contact_id !== null ? (
          <>
            <Button size="sm" onClick={actions.onDone} disabled={state.busy}>Done</Button>
            <Button size="sm" variant="outline" onClick={actions.onDrop} disabled={state.busy}>Drop</Button>
          </>
        ) : (
          <span className="text-xs text-gray-600">{NO_CONTACT}</span>
        )}
        <LaterMenu onPick={actions.onLater} disabled={state.busy} />
        {/* Task 9: his contact's thread with a draft for this to-do waiting in the box - ⛔ never sent by itself. R14: no
            contact, no thread to draft into. */}
        {todo.ghl_contact_id !== null && (
          <Link
            to={`/crm/conversations?${new URLSearchParams({ contact: todo.ghl_contact_id, todo: todo.ghl_task_id })}`}
            className="text-sm font-medium text-primary-600 hover:underline"
          >
            Draft message
          </Link>
        )}
      </div>
      {state.error && <p role="alert" className="mt-2 text-sm text-red-700">{state.error}</p>}
    </li>
  );
}

/** The to-dos closed by a tap since the list was last read, and how. */
export type ClosedTodos = Record<string, 'done' | 'dropped'>;

interface Props {
  todos: TodoItem[];
  /** What he closed in the app in the last 7 days (the server's list): the greyed Done section. */
  finished?: FinishedTodo[];
  /** A Reopen landed in GoHighLevel: the page re-reads, and the to-do comes back in the list. */
  onReopened?: () => void;
  /**
   * ⭐ R33: held by the PAGE, so a to-do closed here also leaves the clean-up's cards - with no re-read, which would end
   * its "Marked done. Undo" - and an Undo brings the card back. The page clears it on every read of the list.
   */
  closed: ClosedTodos;
  onClosed: (id: string, how: 'done' | 'dropped' | null) => void;
}

const SHOW_FIRST = 15;
type KindFilter = 'all' | TodoKind;

/**
 * One finished to-do, greyed: what it was, how it ended and when, and a way back. ⛔ Reopen reaches GoHighLevel first (the
 * server's rule) - a refusal stays here with the server's words and the page is not re-read as if it had landed.
 */
function FinishedRow({ item, onReopened }: { item: FinishedTodo; onReopened: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reopen = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.todos.reopen(item.ghl_task_id);
      onReopened();
    } catch (e) {
      setError(wordsOf(e, NOT_SAVED));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-500">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <p className="break-words line-through">{item.title}</p>
          <p className="break-words text-xs">{item.contact_name ?? 'No contact'}</p>
        </div>
        <span className="text-xs font-medium">{`${item.how === 'done' ? 'Done' : 'Dropped'} ${dueDay(item.closed_at)}`}</span>
        <Button size="sm" variant="outline" onClick={() => void reopen()} disabled={busy}>Reopen</Button>
      </div>
      {error && <p role="alert" className="mt-1 text-sm text-red-700">{error}</p>}
    </li>
  );
}

/**
 * His words, 2026-10-24: "I just deleted a few ... it would be good to see it grayed out or go to the bottom". What he
 * closed in the app in the last 7 days stays here - greyed, newest first, each with Reopen.
 */
function FinishedSection({ items, onReopened }: { items: FinishedTodo[]; onReopened: () => void }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, SHOW_FIRST);
  return (
    <section aria-label={`Done and dropped, last 7 days (${items.length})`} className="mt-8">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">{`Done and dropped, last 7 days (${items.length})`}</h2>
      <ul className="space-y-2">{shown.map((f) => <FinishedRow key={f.ghl_task_id} item={f} onReopened={onReopened} />)}</ul>
      {items.length > SHOW_FIRST && !all && (
        <Button size="sm" variant="ghost" className="mt-2" onClick={() => setAll(true)}>{`Show all ${items.length} done and dropped`}</Button>
      )}
    </section>
  );
}

/**
 * His open to-dos (spec 4.1) in day groups - due today, overdue (the last 7 days / more than a week), coming up, no due
 * date - with type chips and a search, and the snoozed ones in a Later group. What he closed stays below, greyed.
 * ⛔ A to-do leaves only after the server says yes; on an error it stays, with the server's words beside it.
 * ⭐ "Marked done. Undo" lasts until the next read of the list and no longer (the page remounts this on every read).
 */
export default function TodoList({ todos, finished = [], onReopened = () => undefined, closed, onClosed }: Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [snoozed, setSnoozed] = useState<Record<string, string>>({});
  const [kind, setKind] = useState<KindFilter>('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Partial<Record<TodoGroupKey, boolean>>>({});

  const run = async (id: string, write: () => Promise<void>) => {
    setBusy((b) => ({ ...b, [id]: true }));
    setErrors(({ [id]: _gone, ...rest }) => rest);
    try {
      await write();
    } catch (e) {
      setErrors((er) => ({ ...er, [id]: wordsOf(e, NOT_SAVED) }));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const actionsFor = (id: string): TodoRowActions => ({
    onDone: () => void run(id, async () => {
      await api.todos.done(id);
      onClosed(id, 'done');
    }),
    onDrop: () => void run(id, async () => {
      await api.todos.drop(id);
      onClosed(id, 'dropped');
    }),
    onUndo: () => void run(id, async () => {
      await api.todos.reopen(id);
      onClosed(id, null);
    }),
    onLater: (when) => void run(id, async () => {
      const { until } = await api.todos.later(id, when);
      setSnoozed((s) => ({ ...s, [id]: until }));
    }),
  });

  const now = Date.now();
  const untilOf = (t: TodoItem) => snoozed[t.ghl_task_id] ?? t.snoozed_until;
  const isLater = (t: TodoItem) => {
    const until = untilOf(t);
    return until !== null && Date.parse(until) > now;
  };
  const row = (t: TodoItem) => (
    <TodoRow
      key={t.ghl_task_id}
      todo={t}
      state={{
        closedAs: closed[t.ghl_task_id] ?? null,
        error: errors[t.ghl_task_id] ?? null,
        busy: busy[t.ghl_task_id] ?? false,
        until: isLater(t) ? untilOf(t) : null,
      }}
      actions={actionsFor(t.ghl_task_id)}
    />
  );

  // The search narrows first; the chips count what the search left, so a chip never promises rows the search hides.
  const searched = todos.filter((t) => matchesQuery(t, query));
  const count = (k: KindFilter) => (k === 'all' ? searched.length : searched.filter((t) => kindOf(t.title) === k).length);
  const shown = searched.filter((t) => kind === 'all' || kindOf(t.title) === kind);
  const open = shown.filter((t) => !isLater(t));
  const later = shown.filter(isLater);
  const groups = groupTodos(open, new Date());
  const searching = query.trim() !== '';
  const chips: [KindFilter, string][] = [['all', 'All'], ['call', 'Calls'], ['message', 'Texts and emails'], ['reengage', 'Re-engage'], ['other', 'Other']];

  return (
    <>
      {todos.length > 0 && (
        <div className="mb-4 space-y-2">
          <input
            type="search"
            aria-label="Search tasks"
            placeholder="Search by contact or task"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            {chips.map(([k, label]) => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
                className={`rounded-full border px-3 py-1 text-sm ${kind === k ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700' : 'border-gray-300 text-gray-700'}`}
              >
                {`${label} (${count(k)})`}
              </button>
            ))}
          </div>
        </div>
      )}
      {groups.length > 0 && (
      <ul aria-label="Open tasks">
      {groups.map((g) => {
        // A search opens every group that has a match: a result hidden in a closed group reads as no result.
        const isOpen = searching || (expanded[g.key] ?? !g.startsCollapsed);
        return (
          <li key={g.key} role="presentation" className="mb-5 list-none">
          <section>
            <h2 className="mb-2">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setExpanded((e) => ({ ...e, [g.key]: !isOpen }))}
                className="text-sm font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
              >
                {`${isOpen ? '\u25BE' : '\u25B8'} ${g.label} (${g.rows.length})`}
              </button>
            </h2>
            {isOpen && <ul aria-label={g.label} className="space-y-3">{g.rows.map(row)}</ul>}
          </section>
          </li>
        );
      })}
      </ul>
      )}
      {open.length === 0 && (
        <p className="py-10 text-center text-sm text-gray-500">{shown.length === 0 && todos.length > 0 ? 'No tasks match.' : 'No open tasks.'}</p>
      )}
      {later.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Later</h2>
          <ul aria-label="Later" className="space-y-3">{later.map(row)}</ul>
        </section>
      )}
      {finished.length > 0 && <FinishedSection items={finished} onReopened={onReopened} />}
    </>
  );
}
